import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { positionsApi, professionClassifier, type Position } from '@/api/hr';
import { useConfirm } from '@/components/ConfirmProvider';
import Drawer from '@/components/Drawer';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { nextRefCode } from '@/utils/nextRefCode';

interface Editing {
  id?: string;
  code: string;
  name: string;
  kp_code: string | null;
  kp_professional_name: string | null;
}

export default function PositionsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  // 2026-08-23 (Норма #29): серверна пагінація + сортування.
  const refreshRef = useRef<(() => void) | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<Position>(
    (limit, offset, sort) => positionsApi.list({
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [],
  );
  const refreshList = () => {
    refreshRef.current?.();
    qc.invalidateQueries({ queryKey: ['positions'] });
  };
  const [editing, setEditing] = useState<Editing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => editing!.id
      ? positionsApi.update(editing!.id, {
          code: editing!.code, name: editing!.name,
          kp_code: editing!.kp_code,
          kp_professional_name: editing!.kp_professional_name,
        })
      : positionsApi.create({
          code: editing!.code, name: editing!.name,
          kp_code: editing!.kp_code,
          kp_professional_name: editing!.kp_professional_name,
        }),
    onSuccess: () => {
      refreshList();
      setEditing(null);
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => positionsApi.remove(id),
    onSuccess: () => refreshList(),
  });

  const openEditor = (p: Position) => setEditing({
    id: p.id, code: p.code, name: p.name,
    kp_code: p.kp_code, kp_professional_name: p.kp_professional_name,
  });

  const cols = useMemo<ColDef<Position>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: Position }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'code', headerName: 'Код', width: 100 },
    { field: 'name', headerName: 'Назва посади', flex: 1, minWidth: 240 },
    {
      field: 'kp_code', headerName: 'КП код', width: 100,
      cellRenderer: (p: { data?: Position }) => p.data
        ? <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{p.data.kp_code ?? '—'}</span> : '',
    },
    // Колонку «Професійна назва (КП)» прибрано (запит власника).
    // Iконки ✎/× прибрано — редагування через double-click по рядку.
  ], []);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Посади<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
                onClick={async () => {
                  const all = await positionsApi.list({ limit: 2000 });
                  setEditing({
                  // Автонумерація коду (як у 1С) — можна перезаписати вручну.
                  code: nextRefCode(all.items.map((p) => p.code)),
                  name: '', kp_code: null, kp_professional_name: null,
                });
                }}>
          + Новий
        </button>
      </div>

      {editing && (
        <Drawer
          open={!!editing}
          title={editing.id ? `Посада: ${editing.name || '—'}` : 'Нова посада'}
          onClose={() => { setEditing(null); setError(null); }}
          footer={
            <>
              {editing.id && (
                <button className="danger"
                        onClick={() => confirm('Видалити посаду?').then((ok) => {
                          if (ok && editing.id) { remove.mutate(editing.id); setEditing(null); }
                        })}>
                  Видалити
                </button>
              )}
              <button onClick={() => { setEditing(null); setError(null); }}>Скасувати</button>
              <button className="primary" disabled={save.isPending}
                      onClick={() => save.mutate()}>
                Зберегти
              </button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label>Код
              <input placeholder="Код" value={editing.code}
                     onChange={(e) => setEditing({ ...editing, code: e.target.value })} />
            </label>
            <label>Назва посади (як у штатному розкладі)
              <input placeholder="напр. Старший програміст" value={editing.name}
                     onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </label>
            <label>Професійна назва (КП, ДК 003:2010)
              <KpCodePicker
                kpCode={editing.kp_code}
                professionalName={editing.kp_professional_name}
                onChange={(kp, prof) => setEditing({
                  ...editing, kp_code: kp, kp_professional_name: prof,
                })} />
            </label>
            {error && <div className="error">{error}</div>}
          </div>
        </Drawer>
      )}

      <InfiniteDocGrid<Position>
        datasource={datasource}
        columnDefs={cols}
        storageKey="positions-list"
        onRowDoubleClicked={openEditor}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
        refreshRef={refreshRef}
      />
    </div>
  );
}

// ---------------- KpCodePicker ----------------

function KpCodePicker({
  kpCode, professionalName, onChange,
}: {
  kpCode: string | null;
  professionalName: string | null;
  onChange: (kpCode: string | null, professionalName: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const searchQ = useQuery({
    queryKey: ['kp-search', q],
    queryFn: () => professionClassifier.list({ q: q || undefined, limit: 30 }),
    enabled: open,
  });

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const displayText = kpCode
    ? `${kpCode}${professionalName ? ` — ${professionalName}` : ''}`
    : 'КП код (не задано)';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        readOnly
        value={displayText}
        onClick={() => { setOpen(true); setQ(''); }}
        placeholder="Пошук за кодом/назвою…"
        style={{ width: '100%', cursor: 'pointer' }}
      />
      {kpCode && (
        <button onClick={(e) => { e.stopPropagation(); onChange(null, null); }}
                style={{
                  position: 'absolute', right: 4, top: 4,
                  padding: '0.1rem 0.4rem', fontSize: '0.7rem',
                }}>
          ×
        </button>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
          background: 'var(--bg-panel)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', marginTop: 4,
          maxHeight: 360, overflowY: 'auto', boxShadow: 'var(--shadow-lg)',
        }}>
          <input autoFocus value={q}
                 onChange={(e) => setQ(e.target.value)}
                 placeholder="Введіть код або частину назви професії…"
                 style={{ width: '100%', padding: '0.5rem', border: 'none',
                          borderBottom: '1px solid var(--border)',
                          borderRadius: 0 }} />
          {(searchQ.data?.items ?? []).length === 0 ? (
            <div style={{ padding: '0.6rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
              {searchQ.isLoading ? 'Шукаю…' :
               q ? 'Нічого не знайдено.' :
               'Введіть запит. Якщо класифікатор порожній — імпортуйте через /import-csv.'}
            </div>
          ) : (
            (searchQ.data!.items).map((e) => (
              <div key={e.id}
                   onClick={() => {
                     onChange(e.kp_code, e.professional_name);
                     setOpen(false);
                   }}
                   style={{
                     padding: '0.5rem 0.6rem', cursor: 'pointer',
                     borderTop: '1px solid var(--border-soft)', fontSize: '0.85rem',
                   }}
                   onMouseEnter={(ev) => (ev.currentTarget.style.background = 'var(--bg-soft)')}
                   onMouseLeave={(ev) => (ev.currentTarget.style.background = 'white')}>
                <div>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, marginRight: '0.5rem' }}>
                    {e.kp_code}
                  </span>
                  {e.professional_name}
                </div>
                {e.name && (
                  <div style={{ color: 'var(--muted)', fontSize: '0.75rem', marginTop: 2 }}>
                    Група: {e.name}
                  </div>
                )}
              </div>
            ))
          )}
          {searchQ.data && searchQ.data.total > 30 && (
            <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem',
                          color: 'var(--muted)', borderTop: '1px solid var(--border-soft)',
                          background: 'var(--bg-soft-2)' }}>
              Показано 30 з {searchQ.data.total}. Уточніть запит.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
